import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import dayjs, { Dayjs } from "dayjs";
import CommonStyles from "@components/CommonStyles";
import BookingSuccessModal from "@components/Booking/BookingSuccessModal";
import DoctorListModal from "@components/Booking/DoctorListModal";
import SymptomsField from "@components/Booking/SymptomsField";
import TimeSlotGrid from "@components/Booking/TimeSlotGrid";
import BranchPickerModal from "@components/Doctor/[id]/BranchPickerModal";
import DoctorPicker from "@components/PackageBooking/DoctorPicker";
import useCommonSettings from "@base/hooks/useCommonSettings";
import useToggle from "@base/hooks/useToggle";
import { baseRoutes } from "@base/routes/baseRoutes";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import appointmentService from "@base/modules/appointment/appointmentService";
import { ITimeTablePeriod } from "@base/modules/appointment/appointment.interface";
import { apiLangForRecord, bookableSlots, formatVnd, ISymptomSetting, symptomsForSpecialization } from "@base/modules/appointment/bookingCommon";
import { describeBookingError } from "@base/modules/appointment/bookingErrors";
import {
  activeBranches,
  BookingDraft,
  BookingStep,
  buildAppointmentOrder,
  buildAppointmentPayload,
  canCalculateFee,
  canSubmit,
  formatBookingDate,
  hasCurrentFee,
  IFee,
  isPaid,
  pickDoctor,
  selectionKey,
  withDate,
  withDoctor,
  withSite,
  withSlot,
  withVoucher,
} from "@base/modules/appointment/regularBookingRules";
import doctorService from "@base/modules/doctor/doctorService";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import IFilterDcotor from "@base/modules/doctor/interface/filterDoctor.interafce";
import { isDefaultDoctor } from "@base/modules/patientSpecialPackage/packageBookingRules";
import orderService from "@base/modules/order/orderService";
import symptonService from "@base/modules/symptom/symptonService";
import InsuranceCardField from "./InsuranceCardField";
import PromotionModal from "./PromotionModal";

const DOCTOR_PAGE_SIZE = 15;
const SERVER_DATE = "YYYY-MM-DD";

interface IProps {
  draft: BookingDraft;
  onChange: (draft: BookingDraft) => void;
  onGoTo: (step: BookingStep) => void;
  onNext: (draft: BookingDraft, step?: BookingStep) => void;
  /** Leaves the flow (the draft is dropped) for `path`. */
  onDone: (path: string) => void;
}

interface ISuccess {
  key: string;
  title: string;
  description: string;
}

/** The booking screen of a regular booking (§11): record, card, clinic, service, day, doctor, time, code, symptoms. */
export default function BookingForm({ draft, onChange, onGoTo, onNext, onDone }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { hotline } = useCommonSettings();
  const uiLang = i18n.resolvedLanguage;
  const lang = apiLangForRecord(draft.record, uiLang);
  const { open: openLeave, shouldRender: shouldRenderLeave, toggle: toggleLeave } = useToggle();
  const { open: openBranches, shouldRender: shouldRenderBranches, toggle: toggleBranches } = useToggle();
  const { open: openVoucher, shouldRender: shouldRenderVoucher, toggle: toggleVoucher } = useToggle();
  const { open: openAllDoctors, shouldRender: shouldRenderAllDoctors, toggle: toggleAllDoctors } = useToggle();

  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState<boolean>(false);
  const [slots, setSlots] = useState<ITimeTablePeriod[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [isLoadingFee, setIsLoadingFee] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<ISymptomSetting[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<ISuccess>();

  // Async answers land on whatever the draft is by then, not on the draft they started from.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const apply = useCallback(
    (change: (current: BookingDraft) => BookingDraft) => {
      const next = change(draftRef.current);

      draftRef.current = next;
      onChange(next);
    },
    [onChange],
  );

  const branches = activeBranches(draft.site);
  const service = draft.service;

  //! function
  const alertError = useCallback(
    (description: string) => dispatch(setAlertMessage({ type: "error", message: t("Notification"), description })),
    [dispatch, t],
  );

  useEffect(() => {
    if (!draftRef.current.reachedBooking) {
      apply((current) => ({ ...current, reachedBooking: true }));
    }
  }, [apply]);

  const doctorQuery: Omit<IFilterDcotor, "page" | "size"> | undefined = useMemo(() => {
    if (!service || !draft.site?.code) {
      return undefined;
    }

    return {
      doctor_name: "",
      lang,
      appt_date: draft.date,
      site_code: draft.site.code,
      online: !!draft.online,
      specialization_code: service.supported_specialization ?? "",
      ...(service.code ? { service_code: service.code } : {}),
      ...(draft.branch?.code ? { site_branch_code: draft.branch.code } : {}),
    };
  }, [service, draft.site?.code, draft.branch?.code, draft.online, draft.date, lang]);

  // A new day, clinic or service lists the doctors again and picks one: the one booked from, else the first.
  useEffect(() => {
    if (!doctorQuery) {
      setDoctors([]);
      setTotalPage(0);
      return;
    }

    let ignore = false;

    (async () => {
      try {
        setIsLoadingDoctors(true);
        const resp = await doctorService.getList({ ...doctorQuery, page: 0, size: DOCTOR_PAGE_SIZE });
        const list = resp.data.content_page ?? [];

        if (ignore) {
          return;
        }

        setDoctors(list);
        setTotalPage(resp.data.total_page ?? 0);
        apply((current) => {
          const picked = pickDoctor(list, current.preferredDoctorCode, current.doctor?.doctor_code);

          // The doctor booked from is preselected once only.
          return {
            ...withDoctor(current, picked),
            preferredDoctorCode: picked && picked.doctor_code === current.preferredDoctorCode ? null : current.preferredDoctorCode,
          };
        });
      } catch (error) {
        if (!ignore) {
          setDoctors([]);
          apply((current) => withDoctor(current, null));
        }
      } finally {
        if (!ignore) {
          setIsLoadingDoctors(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [doctorQuery, apply]);

  const doctorCode = draft.doctor?.doctor_code;

  const loadSlots = useCallback(async () => {
    if (!doctorCode) {
      setSlots([]);
      return;
    }

    try {
      setIsLoadingSlots(true);
      const resp = await doctorService.getTimeTable(doctorCode, draft.date);

      setSlots(bookableSlots(resp.data.time_table_period ?? [], draft.date));
    } catch (error) {
      setSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [doctorCode, draft.date]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  // The fee of exactly this selection; an answer for an older one is dropped.
  const currentKey = selectionKey(draft);
  const feeNeeded = canCalculateFee(draft) && !hasCurrentFee(draft);

  useEffect(() => {
    if (!feeNeeded) {
      return;
    }

    const key = currentKey;
    let ignore = false;

    (async () => {
      try {
        setIsLoadingFee(true);
        const resp = await appointmentService.calculateFee(buildAppointmentPayload(draftRef.current, { includeInsurance: false }), lang);

        if (!ignore && selectionKey(draftRef.current) === key) {
          apply((current) => ({ ...current, fee: resp.data as IFee, feeKey: key }));
        }
      } catch (error) {
        if (!ignore) {
          alertError(t("booking.genericError"));
        }
      } finally {
        if (!ignore) {
          setIsLoadingFee(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [feeNeeded, currentKey, lang, apply, alertError, t]);

  useEffect(() => {
    symptonService
      .getListSymptom({ lang: uiLang ?? "vi" })
      .then((resp) => setSuggestions(Array.isArray(resp.data) ? resp.data : []))
      .catch(() => setSuggestions([]));
  }, [uiLang]);

  const onBookingError = (error: AxiosError) => {
    if (!error.response) {
      alertError(t("booking.noConnection"));
      return;
    }

    const result = describeBookingError(error.response.data, { translate: t, hotline });

    if (result.kind === "generic") {
      alertError(t("booking.genericError"));
      return;
    }

    alertError(result.text);

    if (result.reloadSlots) {
      apply((current) => withSlot(current, null));
      loadSlots();
    }
  };

  const showSuccess = (appointmentKey: string, appointmentSiteName?: string) => {
    setSuccess({
      key: appointmentKey,
      title: draft.online ? t("booking.success.onlineTitle") : t("booking.success.title"),
      description: draft.online
        ? t("booking.success.onlineDescription")
        : t("booking.success.offlineDescription", {
            date: dayjs(draft.date, SERVER_DATE).format("DD-MM-YYYY"),
            site: appointmentSiteName || draft.site?.name || "",
          }),
    });
  };

  const submit = async () => {
    if (!canSubmit(draft)) {
      return;
    }

    const payload = buildAppointmentPayload(draft, { includeInsurance: true });

    try {
      setIsSubmitting(true);
      await appointmentService.checkExisted(payload);
    } catch (error) {
      setIsSubmitting(false);
      onBookingError(error as AxiosError);
      return;
    }

    // A paid booking is created by the payment step once a method is chosen.
    if (isPaid(draft.fee)) {
      setIsSubmitting(false);
      onNext(draft, "PAYMENT");
      return;
    }

    try {
      const resp = await appointmentService.createAppointment(payload);
      const appointment = resp.data ?? {};

      try {
        await orderService.createOrder(buildAppointmentOrder(draft, appointment, { bankCode: "", totalAmount: 0 }), "vi");
      } catch (orderError) {
        // The appointment stands even if its 0đ order does not; the patient is told nothing failed to book.
      }

      showSuccess(String(appointment.key ?? ""), appointment.site_name);
    } catch (error) {
      onBookingError(error as AxiosError);
    } finally {
      setIsSubmitting(false);
    }
  };

  //! render
  const displayName = (doctor: IDoctor) => (isDefaultDoctor(doctor) ? t("booking.defaultDoctor") : String(doctor.doctor_name ?? ""));
  const record = draft.record;
  const fee = hasCurrentFee(draft) ? draft.fee : null;
  const today = dayjs().startOf("day");

  const renderBlock = (title: string, body: React.ReactNode, onChangeClick?: () => void, changeLabel = t("booking.change")) => (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">{title}</span>
        {onChangeClick ? (
          <button type="button" className="text-common-primary text-sm font-medium font-['Inter']" onClick={onChangeClick}>
            {changeLabel}
          </button>
        ) : null}
      </div>
      <div className="bg-common-background rounded-lg p-3 flex flex-col gap-0.5">{body}</div>
    </div>
  );

  return (
    <div className="flex flex-col gap-5">
      {renderBlock(
        t("booking.record.title"),
        <>
          <span className="text-common-maintext text-sm font-semibold font-['Inter'] uppercase">{record?.patient_name}</span>
          <span className="text-common-subtext text-xs font-['Inter']">
            {[
              record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
              record?.patient_dob ? dayjs(record.patient_dob, SERVER_DATE).format("DD/MM/YYYY") : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </>,
        () => onGoTo("RECORD"),
        t("booking.record.change"),
      )}

      {!draft.online ? (
        <InsuranceCardField value={draft.insurance} record={draft.record} onChange={(insurance) => apply((current) => ({ ...current, insurance }))} />
      ) : null}

      {renderBlock(
        t("Appointment.facility"),
        <>
          <span className="text-common-maintext text-sm font-semibold font-['Inter']">{draft.site?.name}</span>
          {!draft.branch ? <span className="text-common-subtext text-xs font-['Inter']">{draft.site?.address}</span> : null}
        </>,
        () => onGoTo("SITE"),
      )}

      {branches.length
        ? renderBlock(
            t("booking.branch"),
            draft.branch ? (
              <>
                <span className="text-common-maintext text-sm font-semibold font-['Inter']">{draft.branch.name}</span>
                {draft.branch.description ? <span className="text-common-subtext text-xs font-['Inter']">{draft.branch.description}</span> : null}
              </>
            ) : (
              <span className="text-common-subtext text-sm font-['Inter']">{t("booking.chooseBranch")}</span>
            ),
            toggleBranches,
          )
        : null}

      {renderBlock(
        t("Appointment.service"),
        service ? (
          <>
            <span className="text-common-maintext text-sm font-semibold font-['Inter'] line-clamp-1">{service.name}</span>
            {service.description ? <span className="text-common-subtext text-xs font-['Inter'] line-clamp-2">{service.description}</span> : null}
          </>
        ) : (
          <span className="text-common-subtext text-sm font-['Inter']">{t("booking.chooseService")}</span>
        ),
        // Changing the service here lists every service of the clinic, not only the doctor's specialty.
        () => onNext({ ...draft, specializationFilter: "" }, "SERVICE"),
      )}

      <div className="flex flex-col gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {t("booking.serviceDate")} <span className="text-common-red500">*</span>
        </span>
        <CommonStyles.DatePickerUI
          variant="filled"
          allowClear={false}
          value={dayjs(draft.date, SERVER_DATE)}
          format={(value: Dayjs) => formatBookingDate(value.format(SERVER_DATE), uiLang)}
          disabledDate={(current: Dayjs) => !!current && current.isBefore(today, "day")}
          onChange={(value: Dayjs) => value && apply((current) => withDate(current, value.format(SERVER_DATE)))}
        />
      </div>

      <DoctorPicker
        label={t("booking.doctorTitle")}
        emptyText={service ? t("booking.noDoctor") : t("booking.chooseServiceFirst")}
        isLoading={isLoadingDoctors}
        groups={[]}
        doctors={doctors}
        selectedDoctorCode={doctorCode}
        hint={draft.doctor ? displayName(draft.doctor) : undefined}
        displayName={displayName}
        onSeeAll={doctors.length && totalPage > 1 ? toggleAllDoctors : undefined}
        onSelectGroup={() => undefined}
        onSelectDoctor={(doctor) => apply((current) => withDoctor(current, doctor))}
      />

      <div className="flex flex-col gap-2">
        <span className="text-common-maintext text-sm font-medium font-['Inter']">
          {t("booking.preferredTime")} <span className="text-common-red500">*</span>
        </span>
        {doctorCode ? (
          <TimeSlotGrid
            slots={slots}
            selected={draft.slot ?? undefined}
            isLoading={isLoadingSlots}
            onChange={(slot) => apply((current) => withSlot(current, slot ?? null))}
          />
        ) : null}
      </div>

      {renderBlock(
        t("booking.voucher.block"),
        <button
          type="button"
          disabled={!draft.site?.code}
          onClick={toggleVoucher}
          className="flex justify-between items-center gap-2 w-full text-left"
        >
          <span className="text-common-subtext text-sm font-['Inter']">{t("booking.voucher.title")}</span>
          <span className={`text-sm font-medium font-['Inter'] ${draft.voucher ? "text-common-primary" : "text-common-subtext"}`}>
            {draft.voucher?.type ?? t("booking.voucher.placeholder")}
          </span>
        </button>,
        draft.site?.code ? toggleVoucher : undefined,
        draft.voucher ? t("booking.change") : t("booking.voucher.choose"),
      )}

      <SymptomsField
        value={draft.symptoms}
        required
        suggestions={symptomsForSpecialization(suggestions, service?.supported_specialization)}
        onChange={(symptoms) => apply((current) => ({ ...current, symptoms }))}
      />

      <div className="sticky bottom-0 z-10 -mx-4 lg:-mx-8 px-4 lg:px-8 py-4 bg-common-white border-t border-common-divider flex flex-wrap justify-between items-center gap-3">
        <div className="flex flex-col">
          <span className="text-common-subtext text-sm font-['Inter']">{t("booking.total")}</span>
          <span className="text-common-primary text-xl font-semibold font-['Inter']">{isLoadingFee ? "…" : formatVnd(fee?.actual_fee ?? 0)}</span>
        </div>

        <div className="flex gap-3">
          <CommonStyles.ButtonUI
            className="h-12 bg-common-primary100 text-common-primary hover:!bg-common-primary200 hover:!text-common-primary"
            onClick={toggleLeave}
          >
            {t("booking.back")}
          </CommonStyles.ButtonUI>
          <CommonStyles.ButtonUI className="h-12 px-8" loading={isSubmitting} disabled={!canSubmit(draft)} onClick={submit}>
            {t("booking.book")}
          </CommonStyles.ButtonUI>
        </div>
      </div>

      {shouldRenderLeave && (
        <CommonStyles.ModalConfirmationUI
          open={openLeave}
          toggle={toggleLeave}
          title={t("Notification")}
          okText={t("booking.agree")}
          content={t("booking.leaveConfirm")}
          onConfirm={() => {
            toggleLeave();
            onDone(baseRoutes.home);
          }}
        />
      )}

      {shouldRenderBranches && draft.site ? (
        <BranchPickerModal
          open={openBranches}
          toggle={toggleBranches}
          branches={branches}
          onSelect={(branch) => {
            toggleBranches();
            apply((current) => (current.site ? withSite(current, current.site, branch) : current));
          }}
        />
      ) : null}

      {shouldRenderVoucher && draft.site?.code ? (
        <PromotionModal
          open={openVoucher}
          toggle={toggleVoucher}
          online={!!draft.online}
          siteCode={draft.site.code}
          lang={lang}
          current={draft.voucher}
          onApplied={(voucher) => apply((current) => withVoucher(current, voucher))}
        />
      ) : null}

      {shouldRenderAllDoctors && doctorQuery ? (
        <DoctorListModal
          open={openAllDoctors}
          toggle={toggleAllDoctors}
          query={doctorQuery}
          selectedCode={doctorCode}
          displayName={displayName}
          onPick={(doctor) => {
            toggleAllDoctors();
            setDoctors((prev) => (prev.some((item) => item.doctor_code === doctor.doctor_code) ? prev : [...prev, doctor]));
            apply((current) => withDoctor(current, doctor));
          }}
        />
      ) : null}

      <BookingSuccessModal
        open={!!success}
        title={success?.title ?? ""}
        description={success?.description ?? ""}
        detailLabel={t("booking.success.detail")}
        onHome={() => onDone(baseRoutes.home)}
        onDetail={() => onDone(success?.key ? baseRoutes.userAppointmentDetail.replace(":key", success.key) : baseRoutes.userAppointment)}
      />
    </div>
  );
}
